/**
 * Card traits builder for setup-time resolution.
 *
 * Resolves categorical card attributes (hero class, team) from registry
 * data at setup time. Produces G.cardTraits — a sibling snapshot to
 * G.cardStats, G.cardKeywords, G.cardDisplayData.
 *
 * No boardgame.io imports. No registry imports. No .reduce().
 */
import { normalizeTraitSlug } from '../state/traits.normalize.js';
// ---------------------------------------------------------------------------
// Structural guards
// ---------------------------------------------------------------------------
/**
 * Runtime type guard for CardTraitsRegistryReader.
 */
export function isCardTraitsRegistryReader(registry) {
    if (!registry || typeof registry !== 'object')
        return false;
    const candidate = registry;
    return (typeof candidate.listSets === 'function' &&
        typeof candidate.getSet === 'function');
}
// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
/**
 * Parses a qualified ID (setAbbr/slug) into components.
 */
function parseQualifiedId(input) {
    if (typeof input !== 'string' || input.length === 0)
        return null;
    if (input !== input.trim())
        return null;
    const slashIndex = input.indexOf('/');
    if (slashIndex === -1)
        return null;
    if (input.indexOf('/', slashIndex + 1) !== -1)
        return null;
    const setAbbr = input.slice(0, slashIndex);
    const slug = input.slice(slashIndex + 1);
    if (setAbbr.length === 0 || slug.length === 0)
        return null;
    return { setAbbr, slug };
}
/**
 * Finds the hero entry in set data by slug.
 */
function findHeroEntry(setData, heroSlug) {
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.heroes))
        return null;
    for (const entry of candidate.heroes) {
        if (!entry || typeof entry !== 'object')
            continue;
        const heroEntry = entry;
        if (typeof heroEntry.slug !== 'string')
            continue;
        if (heroEntry.slug !== heroSlug)
            continue;
        if (!Array.isArray(heroEntry.cards))
            continue;
        return heroEntry;
    }
    return null;
}
/**
 * Defensively parses physicalCards into typed entries.
 */
function parsePhysicalCards(raw) {
    if (!Array.isArray(raw))
        return [];
    const result = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object')
            continue;
        const candidate = item;
        if (typeof candidate.count !== 'number' || candidate.count < 1)
            continue;
        if (!Array.isArray(candidate.sides) || candidate.sides.length === 0)
            continue;
        result.push({
            count: candidate.count,
            sides: candidate.sides,
        });
    }
    return result;
}
/**
 * Finds the card entry by slug in a hero's cards array.
 */
function findCardEntryBySlug(cards, slug) {
    for (const card of cards) {
        if (card.slug === slug)
            return card;
    }
    return null;
}
// ---------------------------------------------------------------------------
// buildCardTraits — main builder
// ---------------------------------------------------------------------------
// why: fan-out per copy is required because zone entries are copy-suffixed
// ext_ids (e.g., core/black-widow/mission-accomplished#0). Without per-copy
// entries, every zone lookup returns undefined and superpowers silently fail.
/**
 * Builds the G.cardTraits lookup table from registry data at setup time.
 *
 * Enumerates the same CardExtId universe as buildCardStats() (single
 * enumeration authority) and fans out per copy using the #N suffix pattern.
 *
 * @param registry - Registry reader (structural interface).
 * @param config - Validated match setup config.
 * @returns Record mapping each copy-suffixed CardExtId to its CardTraitEntry.
 */
export function buildCardTraits(registry, config) {
    if (!isCardTraitsRegistryReader(registry)) {
        return {};
    }
    const traits = {};
    for (const heroDeckId of config.heroDeckIds) {
        const parsed = parseQualifiedId(heroDeckId);
        if (parsed === null)
            continue;
        const setData = registry.getSet(parsed.setAbbr);
        if (!setData || typeof setData !== 'object')
            continue;
        const heroEntry = findHeroEntry(setData, parsed.slug);
        if (heroEntry === null)
            continue;
        const team = (typeof heroEntry.team === 'string' && heroEntry.team.length > 0)
            ? normalizeTraitSlug(heroEntry.team)
            : null;
        const physicalCards = parsePhysicalCards(heroEntry.physicalCards);
        if (physicalCards.length > 0) {
            for (const physicalCard of physicalCards) {
                // why: WP-724 / D-24545 — emit a traits entry for EACH face (sides[0] primary,
                // and sides[1] when split), so a class-gated effect fired by the CHOSEN face reads
                // that face's printed class (heroClass / heroClass2) from G.cardTraits. Solo cards
                // have one side and emit one entry — byte-unchanged.
                for (const sideSlug of physicalCard.sides) {
                    const cardEntry = findCardEntryBySlug(heroEntry.cards, sideSlug);
                    const heroClass = (cardEntry !== null && typeof cardEntry.hc === 'string' && cardEntry.hc.length > 0)
                        ? normalizeTraitSlug(cardEntry.hc)
                        : null;
                    // why: WP-703 / D-24523 — the dual-class card's second printed class, omitted
                    // from the entry when absent so single-class cards serialize byte-identically.
                    const heroClass2 = (cardEntry !== null && typeof cardEntry.hc2 === 'string' && cardEntry.hc2.length > 0)
                        ? normalizeTraitSlug(cardEntry.hc2)
                        : null;
                    const baseExtId = `${parsed.setAbbr}/${parsed.slug}/${sideSlug}`;
                    for (let copyIndex = 0; copyIndex < physicalCard.count; copyIndex++) {
                        const extId = `${baseExtId}#${copyIndex}`;
                        traits[extId] = heroClass2 !== null ? { heroClass, heroClass2, team } : { heroClass, team };
                    }
                }
            }
        }
        else {
            for (const card of heroEntry.cards) {
                const heroClass = (typeof card.hc === 'string' && card.hc.length > 0)
                    ? normalizeTraitSlug(card.hc)
                    : null;
                // why: WP-703 / D-24523 — second printed class, omitted-when-absent (see above).
                const heroClass2 = (typeof card.hc2 === 'string' && card.hc2.length > 0)
                    ? normalizeTraitSlug(card.hc2)
                    : null;
                const baseExtId = `${parsed.setAbbr}/${parsed.slug}/${card.slug}`;
                traits[baseExtId] = heroClass2 !== null ? { heroClass, heroClass2, team } : { heroClass, team };
            }
        }
    }
    return traits;
}
//# sourceMappingURL=buildCardTraits.js.map